import { useState, useEffect, useCallback } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import ListingCard from '../components/listings/ListingCard';
import SellerAnalyticsDashboard from '../components/SellerAnalyticsDashboard';
import EditListingModal from '../components/EditListingModal';
import PromotionPurchase from '../components/PromotionPurchase';
import PromotionAnalytics from '../components/PromotionAnalytics';
import CouponManager from '../components/CouponManager';
import TwoFactorSetup from '../components/TwoFactorSetup';
import ProfileSettings from '../components/ProfileSettings';
import BillingSettings from '../components/BillingSettings';
import BarterTradesPage from './BarterTradesPage';
import FreeClaimsPage from './FreeClaimsPage';

const TABS = [
  { id:'overview',       label:'Overview',       icon:'📊' },
  { id:'analytics',      label:'Shop Analytics', icon:'📈' },
  { id:'feed',           label:'Following Feed', icon:'🗞️' },
  { id:'listings',       label:'My Listings',    icon:'📋' },
  { id:'promotions',     label:'Promotions',     icon:'⭐' },
  { id:'bookings',       label:'Bookings',       icon:'📅' },
  { id:'orders',         label:'Orders',         icon:'📦' },
  { id:'bids',           label:'My Bids',        icon:'🔨' },
  { id:'auctions-won',   label:'Auctions Won',   icon:'🏆' },
  { id:'offers',         label:'Offers',         icon:'💰' },
  { id:'reviews',        label:'Reviews',        icon:'⭐' },
  { id:'my-reviews',     label:'My Reviews',     icon:'📝' },
  { id:'barter',         label:'Barter Trades',  icon:'🔄' },
  { id:'free',           label:'Free Claims',    icon:'🆓' },
  { id:'payouts',        label:'Payouts',        icon:'💸' },
  { id:'following',      label:'Following',      icon:'👥' },
  { id:'bundles',        label:'Bundle Deals',   icon:'🎁' },
  { id:'templates',      label:'Templates',      icon:'📋' },
  { id:'saved',          label:'Saved',          icon:'❤️' },
  { id:'profile',        label:'Profile',        icon:'👤' },
  { id:'billing',        label:'Billing',        icon:'💳' },
  { id:'notifications',  label:'Notifications',  icon:'🔔' },
  { id:'security',       label:'Security',       icon:'🔐' },
];

function BundleForm({ existing, onSave, onDelete }) {
  const [minItems, setMinItems] = useState(existing?.min_items || 2);
  const [pct, setPct] = useState(existing?.discount_percent || 10);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await API.post('/users/me/bundle-discount', { minItems: parseInt(minItems), discountPercent: parseFloat(pct) });
      onSave(data.bundleDiscount);
      setMsg('✅ Saved!');
    } catch { setMsg('Failed to save'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const remove = async () => {
    if (!window.confirm('Remove your bundle discount?')) return;
    await API.delete('/users/me/bundle-discount');
    onDelete();
  };

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
        <div className="form-group">
          <label className="form-label">Minimum items</label>
          <input className="form-input" type="number" min={2} max={10} value={minItems} onChange={e => setMinItems(e.target.value)} />
          <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:3 }}>Minimum to qualify</div>
        </div>
        <div className="form-group">
          <label className="form-label">Discount %</label>
          <input className="form-input" type="number" min={1} max={50} value={pct} onChange={e => setPct(e.target.value)} />
          <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:3 }}>Applied to subtotal</div>
        </div>
      </div>
      {msg && <div className={`alert ${msg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom:10 }}>{msg}</div>}
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-cherry" onClick={save} disabled={saving}>{saving ? 'Saving…' : existing ? '💾 Update' : '✅ Enable Bundle Discount'}</button>
        {existing && <button className="btn btn-ghost" style={{ color:'var(--cherry)' }} onClick={remove}>Remove</button>}
      </div>
    </div>
  );
}

function RateBuyerButton({ order }) {
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    API.get(`/reviews/check?referenceId=${order.id}&referenceType=order`)
      .then(r => { setDone(r.data.reviewed); setChecked(true); }).catch(() => setChecked(true));
  }, [order.id]);

  if (!checked || done) return done ? <span style={{ fontSize:11, color:'var(--text-soft)' }}>⭐ Buyer rated</span> : null;
  if (!open) return (
    <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>⭐ Rate Buyer</button>
  );
  return (
    <div style={{ background:'#FFF8F0', border:'1px solid var(--border)', borderRadius:10, padding:14, width:'100%', marginTop:8 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>⭐ Rate this buyer</div>
      <div style={{ display:'flex', gap:6, marginBottom:10 }}>
        {[1,2,3,4,5].map(s => (
          <button key={s} onClick={() => setRating(s)}
            style={{ fontSize:22, background:'none', border:'none', cursor:'pointer', opacity: s <= rating ? 1 : 0.3 }}>★</button>
        ))}
      </div>
      <textarea className="form-input" rows={2} placeholder="Optional comment..." value={comment}
        onChange={e => setComment(e.target.value)} style={{ fontSize:13, marginBottom:10, resize:'vertical' }} />
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-cherry btn-sm" disabled={loading} onClick={async () => {
          setLoading(true);
          try {
            await API.post('/reviews', {
              referenceId: order.id, referenceType: 'order',
              revieweeId: order.buyer_id, listingId: order.listing_id,
              rating, body: comment, reviewType: 'seller_to_buyer',
            });
            setDone(true); setOpen(false);
          } catch (err) { alert(err.response?.data?.error || 'Failed'); }
          setLoading(false);
        }}>{loading ? 'Submitting…' : 'Submit Rating'}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  useSEO({ title: 'Dashboard' });
  const { tab = 'overview' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const push = usePushNotifications();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState(tab);
  const [data, setData]   = useState({});
  const [loading, setLoading] = useState(true);
  const [editingListing, setEditingListing] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPromotionPurchase, setShowPromotionPurchase] = useState(false);
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [bulkPrice, setBulkPrice] = useState('');
  const [idDocType, setIdDocType] = useState('drivers_license');
  const [idFrontUrl, setIdFrontUrl] = useState('');
  const [idBackUrl, setIdBackUrl] = useState('');
  const [idSelfieUrl, setIdSelfieUrl] = useState('');
  const [idUploading, setIdUploading] = useState({});
  const [idSubmitting, setIdSubmitting] = useState(false);
  const [idMsg, setIdMsg] = useState(null);

  // Counter-offer inline form
  const [counteringOfferId, setCounteringOfferId] = useState(null);
  const [counterAmt, setCounterAmt] = useState('');
  const [counterMsg, setCounterMsg] = useState('');

  // Return request inline form
  const [returningOrderId, setReturningOrderId] = useState(null);
  const [returnReason, setReturnReason] = useState('item_not_as_described');
  const [returnDesc, setReturnDesc] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  // Return rejection inline form
  const [rejectingReturnId, setRejectingReturnId] = useState(null);
  const [rejectReturnNote, setRejectReturnNote] = useState('');

  // Shipping label
  const [shippingOrderId, setShippingOrderId] = useState(null);
  const [shippingForm, setShippingForm] = useState({ weightLbs:'1', lengthIn:'10', widthIn:'8', heightIn:'4' });
  const [shippingRates, setShippingRates] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingBuying, setShippingBuying] = useState(null);
  const [shippingResult, setShippingResult] = useState({});
  const [shippingError, setShippingError] = useState(null);
  const [shipmentId, setShipmentId] = useState(null);

  const fetchRates = async (order) => {
    setShippingLoading(true); setShippingRates([]); setShippingError(null);
    try {
      const { data } = await API.post('/shipping/rates', {
        fromName: user.first_name + ' ' + user.last_name,
        fromZip: user.zip || '90001', fromCity: user.city || 'Los Angeles', fromState: user.state || 'CA',
        toName: order.other_first_name,
        toZip: order.buyer_zip || '10001', toCity: order.buyer_city || 'New York', toState: order.buyer_state || 'NY',
        ...shippingForm,
      });
      setShipmentId(data.shipmentId);
      setShippingRates(data.rates || []);
    } catch (err) { setShippingError(err.response?.data?.error || 'Failed to get rates'); }
    setShippingLoading(false);
  };

  const buyLabel = async (order, rateId) => {
    setShippingBuying(rateId);
    try {
      const { data } = await API.post('/shipping/label', { rateId, orderId: order.id });
      setShippingResult(r => ({ ...r, [order.id]: data }));
      setData(p => ({ ...p, orders: p.orders.map(x => x.id === order.id ? { ...x, status:'shipped', tracking_number: data.trackingNumber } : x) }));
      setShippingOrderId(null); setShippingRates([]);
    } catch (err) { setShippingError(err.response?.data?.error || 'Failed to buy label'); }
    setShippingBuying(null);
  };

  // Payout connect
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Auto-pricing rules panel
  const [autoPriceListingId, setAutoPriceListingId] = useState(null);
  const [autoPriceRule, setAutoPriceRule] = useState(null);
  const [autoPriceLoading, setAutoPriceLoading] = useState(false);
  const [autoPriceForm, setAutoPriceForm] = useState({ daysUnsold:'', dropPct:'', minPrice:'', autoAcceptAbove:'', autoDeclineBelow:'', enabled:true });
  const [autoPriceSaved, setAutoPriceSaved] = useState(false);

  useEffect(() => { setActiveTab(tab); }, [tab]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    const fetchers = {
      overview:      () => Promise.all([API.get('/users/me/listings?status=active&limit=4'), API.get('/users/me/bookings'), API.get('/users/me/orders')]).then(([l,b,o]) => ({ listings: l.data.listings, bookings: b.data.bookings, orders: o.data.orders })),
      analytics:     () => Promise.all([API.get('/users/me/analytics?period=30'), API.get('/analytics/shop?period=30')]).then(([a, s]) => ({ ...a.data, shopViews: s.data.viewsSeries, totalWatches: s.data.totalWatches, totalSaves: s.data.totalSaves, perListingStats: s.data.topListings })),
      listings:      () => Promise.all([API.get('/users/me/listings?status=active'), API.get('/users/me/listings?status=sold'), API.get('/users/me/listings?status=draft'), API.get('/users/me/listings?status=archived')]).then(([a,s,d,ar]) => ({ activeListings: a.data.listings, soldListings: s.data.listings, draftListings: d.data.listings, archivedListings: ar.data.listings })),
      bookings:      () => API.get('/bookings?owner=me').then(r => ({ bookings: r.data.bookings })),
      orders:        () => Promise.all([API.get('/users/me/orders'), API.get('/returns/mine')]).then(([o,r]) => ({ orders: o.data.orders, returns: r.data.returns })),
      bids:          () => API.get('/users/me/bids').then(r => ({ bids: r.data.bids })),
      'auctions-won': () => API.get('/users/me/auctions-won').then(r => ({ auctions: r.data.auctions })),
      offers:        () => Promise.all([API.get('/offers/mine'), API.get('/offers/listing/all').catch(() => ({ data: { offers: [] } }))]).then(([mine, incoming]) => ({ myOffers: mine.data.offers, incomingOffers: incoming.data.offers })),
      barter:        () => Promise.resolve({}),
      free:          () => Promise.resolve({}),
      payouts:       () => Promise.all([API.get('/users/me/payouts'), API.get('/users/me/stripe-connect/status').catch(() => ({ data: { connected: false } }))]).then(([p, s]) => ({ ...p.data, stripeConnect: s.data })),
      feed:          () => API.get('/users/me/feed').then(r => ({ feedListings: r.data.listings })),
      following:     () => API.get('/users/me/following').then(r => r.data),
      bundles:       () => API.get('/users/me/bundle-discount').then(r => r.data),
      templates:     () => API.get('/users/me/templates').then(r => ({ templates: r.data.templates })),
      saved:         () => API.get('/users/me/saved').then(r => ({ listings: r.data.listings })),
      profile:       () => Promise.resolve({}),
      billing:       () => Promise.resolve({}),
      notifications: () => API.get('/notifications').then(r => ({ notifications: r.data.notifications })),
      security:      () => API.get('/users/me/verify-id').then(r => ({ idVerification: r.data })).catch(() => ({ idVerification: null })),
    };
    (fetchers[activeTab] || fetchers.overview)()
      .then(d => setData(d))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [activeTab, user]);

  const uploadIdDoc = async (file, field) => {
    if (!file) return;
    setIdUploading(p => ({ ...p, [field]: true }));
    setIdMsg(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await API.post('/upload/listing-image', fd);
      if (field === 'front') setIdFrontUrl(data.url);
      if (field === 'back') setIdBackUrl(data.url);
      if (field === 'selfie') setIdSelfieUrl(data.url);
    } catch {
      setIdMsg({ type: 'error', text: 'Upload failed. Please try again.' });
    }
    setIdUploading(p => ({ ...p, [field]: false }));
  };

  const markAllRead = async () => {
    await API.patch('/notifications/read-all');
    setData(p => ({ ...p, notifications: p.notifications?.map(n => ({ ...n, is_read: true })) }));
  };

  const statusPill = (status) => {
    const map = { pending:'pill-pending', confirmed:'pill-active', active:'pill-active', completed:'pill-paid', cancelled:'pill-pending', shipped:'pill-escrow', return_requested:'pill-pending', refunded:'pill-paid', draft:'pill-pending' };
    return <span className={`pill ${map[status]||'pill-pending'}`}><span className="pill-dot"/>{status}</span>;
  };

  if (!user) return null;

  return (
    <div style={{ maxWidth:1200, margin:'0 auto', padding:'24px 24px 80px' }} className='dashboard-page'>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:32 }}>
        <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
          {user.avatarUrl
            ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${user.avatarUrl}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
            : user.firstName?.[0]?.toUpperCase()
          }
        </div>
        <div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700, marginBottom:2 }}>
            Welcome back, {user.firstName}
          </h1>
          <div style={{ fontSize:13, color:'var(--text-soft)' }}>
            {user.emailVerified ? '✅ Email verified' : '⚠️ Email not verified · '}
            {!user.emailVerified && <a style={{ color:'var(--cherry)', cursor:'pointer', fontWeight:600 }}>Resend verification</a>}
          </div>
        </div>
        <button className="btn btn-cherry btn-sm" style={{ marginLeft:'auto' }} onClick={() => navigate('/list')}>+ List item</button>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom:32 }}>
        {TABS.map(t => (
          <button key={t.id} className={`tab-btn ${activeTab===t.id?'active':''}`}
            onClick={() => { setActiveTab(t.id); navigate(`/dashboard/${t.id}`); }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Back to overview link for non-overview tabs */}
      {activeTab !== 'overview' && (
        <button onClick={() => { setActiveTab('overview'); navigate('/dashboard'); }}
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cherry)', fontWeight:600, fontSize:13, marginBottom:16, padding:0, display:'flex', alignItems:'center', gap:4 }}>
          ← Overview
        </button>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0' }}>
          <div style={{ fontSize:40, marginBottom:12 }} className="float-anim">🍒</div>
          <div style={{ fontSize:14, color:'var(--text-soft)' }}>Loading...</div>
        </div>
      ) : (
        <>
          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:16, marginBottom:36 }}>
                {[
                  { icon:'📋', label:'Active Listings', value: data.listings?.length || 0, color:'#1A5276', link: '/dashboard/listings' },
                  { icon:'📅', label:'Active Bookings', value: data.bookings?.filter(b=>b.status==='confirmed'||b.status==='active').length || 0, color:'#2D7A3A', link: '/dashboard/bookings' },
                  { icon:'📦', label:'Orders', value: data.orders?.length || 0, color:'#C0392B', link: '/dashboard/orders' },
                  { icon:'⭐', label:'Rating', value: user.ratingAvg ? `${parseFloat(user.ratingAvg).toFixed(1)} ★` : 'No reviews', color:'#C9930A', link: '/dashboard/ratings' },
                ].map(s => (
                  <div key={s.label} onClick={() => s.link && navigate(s.link)} style={{ background:'white', borderRadius:14, border:'1px solid var(--border)', padding:'18px 20px', cursor: s.link ? 'pointer' : 'default', transition: 'all .2s', ':hover': s.link ? { borderColor: s.color, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {} }} onMouseEnter={e => s.link && (e.currentTarget.style.borderColor = s.color, e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')} onMouseLeave={e => s.link && (e.currentTarget.style.borderColor = 'var(--border)', e.currentTarget.style.boxShadow = 'none')}>
                    <div style={{ fontSize:24, marginBottom:8 }}>{s.icon}</div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:s.color, marginBottom:4 }}>{s.value}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)', fontWeight:600, cursor: s.link ? 'pointer' : 'default' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Seller Analytics */}
              <div style={{ marginBottom:32 }}>
                <SellerAnalyticsDashboard />
              </div>

              {/* Free Account Info Card */}
              <div style={{ background:'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)', borderRadius:14, border:'1px solid #4CAF50', padding:24, marginBottom:32 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:24 }}>
                  <div style={{ flex:1 }}>
                    <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, marginBottom:8, color:'#2D7A3A' }}>
                      🎉 You're using a Free Account
                    </h3>
                    <p style={{ fontSize:14, color:'#555', marginBottom:12, lineHeight:1.6 }}>
                      You have full access to browse, buy, rent, and bid. Plus, you can claim free items from the community.
                    </p>
                    <div style={{ fontSize:13, color:'#666', marginBottom:12 }}>
                      <strong>Want to trade with others?</strong> Upgrade anytime for just $20/month or $10 per trade.
                    </div>
                  </div>
                  <button
                    className="btn btn-cherry btn-sm"
                    onClick={()=>navigate('/barter')}
                    style={{ whiteSpace:'nowrap' }}
                  >
                    🔄 Start Trading
                  </button>
                </div>
              </div>

              {data.listings?.length > 0 && (
                <div style={{ marginBottom:32 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>Active Listings</h3>
                    <a style={{ fontSize:13, color:'var(--cherry)', cursor:'pointer', fontWeight:600 }} onClick={()=>navigate('/dashboard/listings')}>View all →</a>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                    {data.listings.slice(0,4).map(l => <ListingCard key={l.id} listing={l} />)}
                  </div>
                </div>
              )}

              {data.bookings?.length === 0 && data.orders?.length === 0 && (
                <div style={{ textAlign:'center', padding:'40px 0', background:'var(--cream)', borderRadius:16, border:'1px dashed var(--border)' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🚀</div>
                  <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:8 }}>Start buying or selling</h3>
                  <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>Browse listings near you or list your first item.</p>
                  <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
                    <button className="btn btn-cherry" onClick={()=>navigate('/browse')}>Browse Listings</button>
                    <button className="btn btn-ghost" onClick={()=>navigate('/list')}>+ List an Item</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── LISTINGS ── */}
          {/* ── ANALYTICS ── */}
          {activeTab === 'analytics' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>📈 Seller Analytics</h3>

              {/* Summary cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:14, marginBottom:28 }}>
                {[
                  { label:'Total Views', value: data.totalViews?.toLocaleString() || 0, icon:'👁', color:'#1A5276' },
                  { label:'Watchlist Adds', value: (data.totalWatches||0).toLocaleString(), icon:'🔔', color:'#C9930A' },
                  { label:'Saves / Likes', value: (data.totalSaves||0).toLocaleString(), icon:'❤️', color:'#C0392B' },
                  { label:'Total Revenue', value: `$${(data.revenueSeries?.reduce((s,r)=>s+parseFloat(r.revenue||0),0)||0).toFixed(2)}`, icon:'💰', color:'#2D7A3A' },
                  { label:'Total Orders', value: data.ordersByStatus?.reduce((s,r)=>s+parseInt(r.count),0)||0, icon:'📦', color:'#8E44AD' },
                ].map(c => (
                  <div key={c.label} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
                    <div style={{ fontSize:24, marginBottom:6 }}>{c.icon}</div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:22, color:c.color }}>{c.value}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Revenue chart (simple bar) */}
              {data.revenueSeries?.length > 0 && (
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:20 }}>
                  <div style={{ fontWeight:700, marginBottom:14 }}>💰 Revenue — Last 30 Days</div>
                  <div style={{ display:'flex', gap:3, alignItems:'flex-end', height:80 }}>
                    {data.revenueSeries.map((r,i) => {
                      const max = Math.max(...data.revenueSeries.map(x=>parseFloat(x.revenue||0)),1);
                      const h = Math.max(4, (parseFloat(r.revenue||0)/max)*76);
                      return <div key={i} title={`${r.date}: $${parseFloat(r.revenue).toFixed(2)}`} style={{ flex:1, height:h, background:'var(--cherry)', borderRadius:'3px 3px 0 0', opacity:.8 }} />;
                    })}
                  </div>
                </div>
              )}

              {/* Order status breakdown */}
              {data.ordersByStatus?.length > 0 && (
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:20 }}>
                  <div style={{ fontWeight:700, marginBottom:12 }}>📦 Orders by Status</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {data.ordersByStatus.map(s => (
                      <div key={s.status} style={{ background:'var(--cream)', borderRadius:8, padding:'6px 12px', fontSize:13 }}>
                        <span style={{ fontWeight:700 }}>{s.count}</span> {s.status}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top listings by revenue */}
              {data.topListings?.length > 0 && (
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:20 }}>
                  <div style={{ fontWeight:700, marginBottom:12 }}>🏆 Top Listings by Revenue</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {data.topListings.map((l,i) => (
                      <div key={l.id} onClick={() => navigate(`/listing/${l.slug || l.id}`)} style={{ display:'flex', gap:12, alignItems:'center', padding:'8px 4px', borderBottom:'1px solid var(--border)', cursor:'pointer', borderRadius:6 }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <span style={{ fontWeight:700, fontSize:18, color:'var(--text-soft)', minWidth:24 }}>#{i+1}</span>
                        {l.primary_image && <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt="" style={{ width:40, height:40, borderRadius:6, objectFit:'cover' }} />}
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:14 }}>{l.title}</div>
                          <div style={{ fontSize:12, color:'var(--text-soft)' }}>{l.view_count} views · {l.order_count} orders</div>
                        </div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700 }}>${parseFloat(l.total_revenue||0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-listing engagement breakdown */}
              {data.perListingStats?.length > 0 && (
                <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  <div style={{ fontWeight:700, marginBottom:12 }}>👁 Listing Engagement (Active)</div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead><tr style={{ background:'var(--cream)' }}>
                        {['Listing','Views','Watchlist','Saves','Price','Status'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {data.perListingStats.map(l => (
                          <tr key={l.id} style={{ borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                            onClick={() => navigate(`/listing/${l.slug || l.id}`)}
                            onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                            <td style={{ padding:'9px 12px', fontWeight:600, maxWidth:220 }}>{l.title}</td>
                            <td style={{ padding:'9px 12px' }}>{l.view_count || 0}</td>
                            <td style={{ padding:'9px 12px' }}>{l.watch_count || 0}</td>
                            <td style={{ padding:'9px 12px' }}>{l.save_count || 0}</td>
                            <td style={{ padding:'9px 12px', fontWeight:700 }}>{l.price ? `$${parseFloat(l.price).toFixed(2)}` : '—'}</td>
                            <td style={{ padding:'9px 12px' }}><span className="pill pill-active" style={{ fontSize:10 }}>{l.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'listings' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>
                  My Listings ({(data.activeListings?.length||0) + (data.soldListings?.length||0) + (data.archivedListings?.length||0)})
                </h3>
                <button className="btn btn-cherry btn-sm" onClick={()=>navigate('/list')}>+ New Listing</button>
              </div>

              {!data.activeListings?.length && !data.soldListings?.length && !data.draftListings?.length ? (
                <div style={{ textAlign:'center', padding:'60px 40px', background:'var(--cream)', borderRadius:16, border:'1px dashed var(--border)' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
                  <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>You haven't listed anything yet.</p>
                  <button className="btn btn-cherry" onClick={()=>navigate('/list')}>List your first item</button>
                </div>
              ) : (
                <>
                  {/* Active Listings */}
                  <div style={{ marginBottom:32 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
                      <h4 style={{ fontSize:16, fontWeight:700 }}>📦 Active ({data.activeListings?.length||0})</h4>
                      {data.activeListings?.length > 0 && (
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedListings(s => s.size === data.activeListings.length ? new Set() : new Set(data.activeListings.map(l=>l.id)))}>
                            {selectedListings.size === data.activeListings.length ? 'Deselect All' : 'Select All'}
                          </button>
                          {selectedListings.size > 0 && (
                            <>
                              <span style={{ fontSize:12, color:'var(--text-soft)' }}>{selectedListings.size} selected</span>
                              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                                <input className="form-input" type="number" placeholder="New price $" value={bulkPrice} onChange={e=>setBulkPrice(e.target.value)} style={{ width:100, padding:'4px 8px', fontSize:12 }} />
                                <button className="btn btn-cherry btn-sm" disabled={!bulkPrice} onClick={async () => {
                                  const ids = Array.from(selectedListings);
                                  await Promise.all(ids.map(id => API.put(`/listings/${id}`, { price: parseFloat(bulkPrice) })));
                                  setData(p => ({ ...p, activeListings: p.activeListings.map(l => selectedListings.has(l.id) ? {...l, price: bulkPrice} : l) }));
                                  setSelectedListings(new Set()); setBulkPrice('');
                                  alert(`Price updated for ${ids.length} listings`);
                                }}>💰 Set Price</button>
                              </div>
                              <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={async () => {
                                if (!window.confirm(`Archive ${selectedListings.size} listings?`)) return;
                                await Promise.all(Array.from(selectedListings).map(id => API.delete(`/listings/${id}`)));
                                setData(p => ({ ...p, activeListings: p.activeListings.filter(l => !selectedListings.has(l.id)) }));
                                setSelectedListings(new Set());
                              }}>🗑️ Archive Selected</button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {!data.activeListings?.length ? (
                      <div style={{ padding:20, background:'var(--cream)', borderRadius:12, color:'var(--text-soft)', textAlign:'center' }}>
                        No active listings
                      </div>
                    ) : (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                        {data.activeListings.map(l => {
                          const typeLabel = l.listing_type === 'auction' ? '🔨 AUCTION' : l.listing_type === 'rent' ? '📅 FOR RENT' : '🛒 FOR SALE';
                          const isSelected = selectedListings.has(l.id);
                          return (
                            <div key={l.id} style={{ position:'relative', outline: isSelected ? '2px solid var(--cherry)' : 'none', borderRadius:12 }}>
                              <div style={{ position:'absolute', top:8, left:8, zIndex:11 }}>
                                <input type="checkbox" checked={isSelected} onChange={e => setSelectedListings(s => { const n=new Set(s); e.target.checked?n.add(l.id):n.delete(l.id); return n; })} style={{ width:16, height:16, cursor:'pointer' }} />
                              </div>
                              <ListingCard listing={l} />
                              <div style={{ position:'absolute', bottom:8, left:8, right:8, background:'white', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', fontSize:11, fontWeight:700, color:'#555', textAlign:'center', zIndex:5 }}>
                                {typeLabel}
                              </div>
                              <button onClick={() => { setEditingListing(l); setShowEditModal(true); }} style={{ position:'absolute', top:8, right:76, background:'white', border:'1px solid var(--border)', borderRadius:8, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16, zIndex:10 }} title="Edit">✏️</button>
                              <button onClick={() => {
                                if (autoPriceListingId === l.id) { setAutoPriceListingId(null); return; }
                                setAutoPriceListingId(l.id); setAutoPriceSaved(false);
                                setAutoPriceLoading(true);
                                API.get(`/auto-pricing/${l.id}`).then(r => {
                                  const rule = r.data.rule;
                                  setAutoPriceRule(rule);
                                  setAutoPriceForm(rule ? { daysUnsold: rule.days_unsold||'', dropPct: rule.drop_pct||'', minPrice: rule.min_price||'', autoAcceptAbove: rule.auto_accept_above||'', autoDeclineBelow: rule.auto_decline_below||'', enabled: rule.enabled } : { daysUnsold:'', dropPct:'', minPrice:'', autoAcceptAbove:'', autoDeclineBelow:'', enabled:true });
                                }).catch(() => {}).finally(() => setAutoPriceLoading(false));
                              }} style={{ position:'absolute', top:8, right:40, background: autoPriceListingId===l.id?'var(--cherry)':'white', border:'1px solid var(--border)', borderRadius:8, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:14, zIndex:10 }} title="Auto-Pricing Rules">📉</button>
                              <button onClick={async () => { if (window.confirm(`Delete "${l.title}"?`)) { try { await API.delete(`/listings/${l.id}`); setData(p => ({ ...p, activeListings: p.activeListings.filter(x => x.id !== l.id) })); } catch { alert('Failed to delete'); } } }} style={{ position:'absolute', top:8, right:8, background:'white', border:'1px solid var(--border)', borderRadius:8, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16, zIndex:10 }} title="Delete">🗑️</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Auto-Pricing Rules Panel */}
                  {autoPriceListingId && data.activeListings?.find(l => l.id === autoPriceListingId) && (
                    <div style={{ background:'#1A1A2E', color:'white', borderRadius:14, padding:24, marginBottom:24 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:16 }}>📉 Auto-Pricing Rules</div>
                          <div style={{ fontSize:13, opacity:.7, marginTop:2 }}>{data.activeListings.find(l=>l.id===autoPriceListingId)?.title}</div>
                        </div>
                        <button onClick={() => setAutoPriceListingId(null)} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', borderRadius:100, padding:'4px 12px', cursor:'pointer', fontSize:12 }}>✕ Close</button>
                      </div>
                      {autoPriceLoading ? (
                        <div style={{ textAlign:'center', padding:20, opacity:.6 }}>Loading...</div>
                      ) : (
                        <>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
                            <div>
                              <label style={{ fontSize:12, opacity:.7, display:'block', marginBottom:4 }}>Drop after (days unsold)</label>
                              <input type="number" min="1" placeholder="e.g. 14" value={autoPriceForm.daysUnsold} onChange={e => setAutoPriceForm(p=>({...p,daysUnsold:e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.08)', color:'white', fontSize:14 }} />
                            </div>
                            <div>
                              <label style={{ fontSize:12, opacity:.7, display:'block', marginBottom:4 }}>Price drop (%)</label>
                              <input type="number" min="1" max="50" placeholder="e.g. 10" value={autoPriceForm.dropPct} onChange={e => setAutoPriceForm(p=>({...p,dropPct:e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.08)', color:'white', fontSize:14 }} />
                            </div>
                            <div>
                              <label style={{ fontSize:12, opacity:.7, display:'block', marginBottom:4 }}>Minimum price ($)</label>
                              <input type="number" min="0" placeholder="Floor price" value={autoPriceForm.minPrice} onChange={e => setAutoPriceForm(p=>({...p,minPrice:e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.08)', color:'white', fontSize:14 }} />
                            </div>
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                            <div>
                              <label style={{ fontSize:12, opacity:.7, display:'block', marginBottom:4 }}>Auto-accept offers above ($)</label>
                              <input type="number" min="0" placeholder="Optional" value={autoPriceForm.autoAcceptAbove} onChange={e => setAutoPriceForm(p=>({...p,autoAcceptAbove:e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.08)', color:'white', fontSize:14 }} />
                            </div>
                            <div>
                              <label style={{ fontSize:12, opacity:.7, display:'block', marginBottom:4 }}>Auto-decline offers below ($)</label>
                              <input type="number" min="0" placeholder="Optional" value={autoPriceForm.autoDeclineBelow} onChange={e => setAutoPriceForm(p=>({...p,autoDeclineBelow:e.target.value}))}
                                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.08)', color:'white', fontSize:14 }} />
                            </div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                              <input type="checkbox" checked={autoPriceForm.enabled} onChange={e => setAutoPriceForm(p=>({...p,enabled:e.target.checked}))} style={{ width:16, height:16 }} />
                              <span style={{ fontSize:13 }}>Enable auto-pricing for this listing</span>
                            </label>
                          </div>
                          {autoPriceSaved && <div style={{ background:'rgba(45,122,58,.3)', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:13 }}>✅ Rules saved!</div>}
                          <div style={{ display:'flex', gap:10 }}>
                            <button style={{ flex:1, background:'var(--cherry)', color:'white', border:'none', borderRadius:8, padding:'10px', fontWeight:700, cursor:'pointer', fontSize:14 }}
                              onClick={async () => {
                                try {
                                  await API.post(`/auto-pricing/${autoPriceListingId}`, { ...autoPriceForm, daysUnsold: autoPriceForm.daysUnsold ? parseInt(autoPriceForm.daysUnsold) : null, dropPct: autoPriceForm.dropPct ? parseFloat(autoPriceForm.dropPct) : null, minPrice: autoPriceForm.minPrice ? parseFloat(autoPriceForm.minPrice) : null, autoAcceptAbove: autoPriceForm.autoAcceptAbove ? parseFloat(autoPriceForm.autoAcceptAbove) : null, autoDeclineBelow: autoPriceForm.autoDeclineBelow ? parseFloat(autoPriceForm.autoDeclineBelow) : null });
                                  setAutoPriceSaved(true);
                                } catch { alert('Failed to save rules'); }
                              }}>
                              💾 Save Rules
                            </button>
                            {autoPriceRule && (
                              <button style={{ background:'rgba(255,255,255,.12)', color:'white', border:'none', borderRadius:8, padding:'10px 16px', cursor:'pointer', fontSize:14 }}
                                onClick={async () => { await API.delete(`/auto-pricing/${autoPriceListingId}`).catch(()=>{}); setAutoPriceRule(null); setAutoPriceForm({ daysUnsold:'', dropPct:'', minPrice:'', autoAcceptAbove:'', autoDeclineBelow:'', enabled:true }); setAutoPriceSaved(false); }}>
                                🗑️ Remove
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Draft Listings */}
                  {data.draftListings?.length > 0 && (
                    <div style={{ marginBottom:28 }}>
                      <h4 style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>📝 Drafts ({data.draftListings.length})</h4>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                        {data.draftListings.map(l => (
                          <div key={l.id} style={{ position:'relative', opacity:0.85 }}>
                            <ListingCard listing={l} />
                            <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.08)', borderRadius:12, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', paddingBottom:12, gap:6 }}>
                              <span style={{ background:'#FFF9E6', color:'#C9930A', fontWeight:700, fontSize:11, padding:'3px 10px', borderRadius:100 }}>DRAFT</span>
                              <div style={{ display:'flex', gap:6 }}>
                                <button className="btn btn-cherry btn-sm" onClick={() => {
                                  if (window.confirm('Publish this listing now?'))
                                    API.post(`/listings/${l.id}/publish`)
                                      .then(() => setData(p => ({ ...p, draftListings: p.draftListings.filter(x => x.id !== l.id), activeListings: [...(p.activeListings||[]), {...l, status:'active'}] })))
                                      .catch(err => alert(err.response?.data?.error || 'Failed to publish'));
                                }}>🚀 Publish</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setEditingListing(l); setShowEditModal(true); }}>✏️ Edit</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sold Listings */}
                  {data.soldListings?.length > 0 && (
                    <div>
                      <h4 style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>✅ Sold ({data.soldListings?.length})</h4>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                        {data.soldListings.map(l => {
                          const typeLabel = l.listing_type === 'auction' ? '🔨 AUCTION' : l.listing_type === 'rent' ? '📅 FOR RENT' : '🛒 FOR SALE';
                          return (
                            <div key={l.id} style={{ position:'relative', opacity:0.7 }}>
                              <ListingCard listing={l} />
                              <div style={{ position:'absolute', bottom:8, left:8, right:8, background:'white', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', fontSize:11, fontWeight:700, color:'#555', textAlign:'center', zIndex:5 }}>
                                {typeLabel}
                              </div>
                              <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.3)', borderRadius:'12px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8 }}>
                                <span style={{ fontSize:20, fontWeight:700, color:'white' }}>SOLD</span>
                                <button className="btn btn-sm" style={{ background:'white', color:'var(--cherry)', border:'none', borderRadius:8, padding:'5px 12px', fontWeight:700, fontSize:12, cursor:'pointer' }}
                                  onClick={() => API.post(`/listings/${l.id}/relist`)
                                    .then(r => { setData(p => ({ ...p, activeListings: [...(p.activeListings||[]), r.data.listing] })); alert('Listing relisted and now active!'); })
                                    .catch(err => alert(err.response?.data?.error || 'Relist failed'))}>
                                  🔁 Relist
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Ended Auctions (no bids) */}
                  {data.archivedListings?.filter(l => l.listing_type === 'auction').length > 0 && (
                    <div style={{ marginTop:28 }}>
                      <h4 style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>⏰ Ended Auctions — No Bids ({data.archivedListings.filter(l => l.listing_type === 'auction').length})</h4>
                      <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:16 }}>These auctions ended without any bids. Relist to try again.</p>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {data.archivedListings.filter(l => l.listing_type === 'auction').map(l => (
                          <div key={l.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:16 }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{l.title}</div>
                              <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                                Starting bid: ${parseFloat(l.starting_bid||0).toLocaleString()}
                                {l.auction_ends_at && ` · Ended ${new Date(l.auction_ends_at).toLocaleDateString()}`}
                              </div>
                            </div>
                            <button
                              className="btn btn-cherry btn-sm"
                              onClick={() => API.post(`/listings/${l.id}/relist`)
                                .then(r => {
                                  setData(p => ({
                                    ...p,
                                    activeListings: [...(p.activeListings||[]), r.data.listing],
                                    archivedListings: p.archivedListings.filter(x => x.id !== l.id),
                                  }));
                                  alert('Auction relisted and now active!');
                                })
                                .catch(err => alert(err.response?.data?.error || 'Relist failed'))}>
                              🔁 Relist
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Other archived (non-auction) */}
                  {data.archivedListings?.filter(l => l.listing_type !== 'auction').length > 0 && (
                    <div style={{ marginTop:28 }}>
                      <h4 style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>🗄️ Archived ({data.archivedListings.filter(l => l.listing_type !== 'auction').length})</h4>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {data.archivedListings.filter(l => l.listing_type !== 'auction').map(l => (
                          <div key={l.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:16 }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{l.title}</div>
                              <div style={{ fontSize:12, color:'var(--text-soft)' }}>${parseFloat(l.price||0).toLocaleString()}</div>
                            </div>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => API.post(`/listings/${l.id}/relist`)
                                .then(r => {
                                  setData(p => ({
                                    ...p,
                                    activeListings: [...(p.activeListings||[]), r.data.listing],
                                    archivedListings: p.archivedListings.filter(x => x.id !== l.id),
                                  }));
                                  alert('Listing relisted and now active!');
                                })
                                .catch(err => alert(err.response?.data?.error || 'Relist failed'))}>
                              🔁 Relist
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop:28 }}>
                    <PromotionAnalytics />
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── PROMOTIONS ── */}
          {activeTab === 'promotions' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>
                💰 Promotions & Discounts
              </h3>
              <div style={{ display:'grid', gap:28 }}>

                {/* Promote a Listing */}
                <div>
                  <h4 style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>⭐ Promote a Listing</h4>
                  {!data.activeListings?.length ? (
                    <div style={{ padding:32, textAlign:'center', background:'var(--cream)', borderRadius:12 }}>
                      <p style={{ color:'var(--text-soft)' }}>You need to create listings first</p>
                    </div>
                  ) : (
                    <div style={{ display:'grid', gap:12 }}>
                      {data.activeListings.map(listing => (
                        <div
                          key={listing.id}
                          style={{
                            padding:16,
                            border:'1px solid var(--border)',
                            borderRadius:12,
                            display:'flex',
                            justifyContent:'space-between',
                            alignItems:'center',
                            gap:16
                          }}
                        >
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>
                              {listing.title}
                            </div>
                            <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                              {listing.category_label} · ${parseFloat(listing.price||0).toLocaleString()}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setEditingListing(listing);
                              setShowPromotionPurchase(true);
                            }}
                            className="btn btn-cherry btn-sm"
                          >
                            ⭐ Promote
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <PromotionAnalytics />
                <CouponManager />
              </div>
            </div>
          )}

          {/* ── BOOKINGS ── */}
          {activeTab === 'bookings' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>Bookings</h3>
              {!data.bookings?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
                  <p style={{ color:'var(--text-soft)' }}>No bookings yet</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {data.bookings.map(b => (
                    <div key={b.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:'18px 20px', display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
                      <div style={{ flex:1, minWidth:200 }}>
                        <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{b.title}</div>
                        <div style={{ fontSize:13, color:'var(--text-soft)' }}>
                          {b.other_first_name} {b.other_last_name} · {b.start_date && format(new Date(b.start_date),'MMM d')} – {b.end_date && format(new Date(b.end_date),'MMM d, yyyy')}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:18 }}>${parseFloat(b.total_charged||0).toFixed(2)}</div>
                        <div style={{ fontSize:12, color:'var(--text-soft)' }}>{b.days} day{b.days!==1?'s':''}</div>
                      </div>
                      {statusPill(b.status)}
                      {['confirmed', 'active'].includes(b.status) && (
                        <button className="btn btn-cherry btn-sm" onClick={() => navigate(`/rentals/${b.id}`)}>
                          📅 Manage
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ORDERS ── */}
          {activeTab === 'orders' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>Orders & Bookings</h3>
              </div>
              {!data.orders?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>📦</div>
                  <p style={{ color:'var(--text-soft)' }}>No orders yet</p>
                  <button className="btn btn-cherry" style={{ marginTop:16 }} onClick={()=>navigate('/browse')}>Start Shopping</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {data.orders.map(o => {
                    const isRental = o.record_type === 'booking';
                    const isBuyer  = isRental ? o.buyer_id === user.id : o.buyer_id === user.id;
                    const hoursSince = (Date.now() - new Date(o.created_at).getTime()) / 3600000;
                    const canCancel  = isBuyer && o.status === 'pending' && hoursSince <= 24 && !isRental;
                    return (
                      <div key={`${o.record_type}-${o.id}`} style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:'18px 20px' }}>
                        <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
                          {/* Thumbnail */}
                          <div style={{ width:64, height:64, borderRadius:10, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 }}>
                            {o.primary_image
                              ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${o.primary_image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                              : (isRental ? '📅' : '📦')
                            }
                          </div>

                          {/* Details */}
                          <div style={{ flex:1, minWidth:160 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, fontSize:15 }}>{o.title}</span>
                              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100,
                                background: isRental ? '#EBF0FF' : '#EAF5EC',
                                color:      isRental ? '#1A5276'  : '#2D7A3A' }}>
                                {isRental ? '📅 Rental' : '🛒 Purchase'}
                              </span>
                            </div>
                            <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:4 }}>
                              {isBuyer ? `From ${o.other_first_name}` : `To ${o.other_first_name}`} · {format(new Date(o.created_at),'MMM d, yyyy')}
                            </div>
                            {isRental && o.start_date && (
                              <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                                📅 {format(new Date(o.start_date),'MMM d')} – {format(new Date(o.end_date),'MMM d, yyyy')} · {o.days} day{o.days!==1?'s':''}
                              </div>
                            )}
                            {o.tracking_number && (
                              <div style={{ fontSize:12, color:'#1A5276', marginTop:3 }}>📦 Tracking: {o.tracking_number}</div>
                            )}
                          </div>

                          {/* Price + status */}
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
                            <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:18 }}>
                              ${parseFloat(o.total_charged||0).toFixed(2)}
                            </div>
                            {statusPill(o.status)}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display:'flex', gap:8, marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)', flexWrap:'wrap' }}>
                          <button className="btn btn-cherry btn-sm"
                            onClick={() => navigate(`/orders/${o.id}?type=${o.record_type}`)}>
                            🧾 View Receipt
                          </button>
                          {o.listing_id && (
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => navigate(`/listing/${o.listing_id}`)}>
                              🔗 View Listing
                            </button>
                          )}
                          {isBuyer && !isRental && ['delivered','completed'].includes(o.status) && o.status !== 'return_requested' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => { setReturningOrderId(o.id); setReturnReason('item_not_as_described'); setReturnDesc(''); }}>↩️ Return</button>
                          )}
                          {returningOrderId === o.id && (
                            <div style={{ marginTop:10, background:'#FDECEA', border:'1px solid rgba(192,57,43,.2)', borderRadius:10, padding:14, width:'100%' }}>
                              <div style={{ fontWeight:700, fontSize:13, marginBottom:10 }}>↩️ Request Return</div>
                              <div className="form-group" style={{ marginBottom:8 }}>
                                <label className="form-label" style={{ fontSize:12 }}>Reason</label>
                                <select className="form-input" value={returnReason} onChange={e => setReturnReason(e.target.value)} style={{ fontSize:13 }}>
                                  <option value="item_not_as_described">Not as described</option>
                                  <option value="damaged_in_shipping">Damaged in shipping</option>
                                  <option value="wrong_item">Wrong item received</option>
                                  <option value="item_not_received">Item not received</option>
                                  <option value="changed_mind">Changed my mind</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div className="form-group" style={{ marginBottom:10 }}>
                                <label className="form-label" style={{ fontSize:12 }}>Description (optional)</label>
                                <textarea className="form-input" rows={2} value={returnDesc} onChange={e => setReturnDesc(e.target.value)} placeholder="Describe the issue..." style={{ fontSize:13, resize:'vertical' }} />
                              </div>
                              <div style={{ display:'flex', gap:8 }}>
                                <button className="btn btn-cherry btn-sm" disabled={returnSubmitting} onClick={async () => {
                                  setReturnSubmitting(true);
                                  try {
                                    await API.post('/returns', { orderId: o.id, reason: returnReason, description: returnDesc, refundType: 'full' });
                                    setData(p => ({ ...p, orders: p.orders.map(x => x.id===o.id ? {...x,status:'return_requested'} : x) }));
                                    setReturningOrderId(null);
                                  } catch (err) { alert(err.response?.data?.error || 'Failed to submit return'); }
                                  setReturnSubmitting(false);
                                }}>{returnSubmitting ? 'Submitting…' : 'Submit Return'}</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => setReturningOrderId(null)}>Cancel</button>
                              </div>
                            </div>
                          )}
                          {o.status === 'delivered' && isBuyer && !isRental && (
                            <button className="btn btn-sm" style={{ background:'var(--green)', color:'white', border:'none', borderRadius:8, padding:'6px 14px', fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer' }}
                              onClick={() => API.patch(`/orders/${o.id}/confirm-receipt`)
                                .then(() => setData(p => ({ ...p, orders: p.orders.map(x => x.id===o.id&&x.record_type==='order' ? {...x, status:'completed'} : x) })))}>
                              ✅ Confirm Receipt
                            </button>
                          )}
                          {canCancel && (
                            <button className="btn btn-sm" style={{ background:'#FDECEA', color:'var(--cherry)', border:'1px solid rgba(192,57,43,.2)', borderRadius:8, padding:'6px 14px', fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer' }}
                              onClick={() => {
                                if (!window.confirm('Cancel this order? This cannot be undone.')) return;
                                API.patch(`/orders/${o.id}/cancel`, { reason: 'Cancelled by buyer' })
                                  .then(() => setData(p => ({ ...p, orders: p.orders.map(x => x.id===o.id&&x.record_type==='order' ? {...x, status:'cancelled'} : x) })))
                                  .catch(err => alert(err.response?.data?.error || 'Could not cancel order'));
                              }}>
                              ✕ Cancel Order
                            </button>
                          )}
                          {canCancel && (
                            <span style={{ fontSize:11, color:'var(--text-soft)', alignSelf:'center' }}>
                              {Math.ceil(24 - hoursSince)}h left to cancel
                            </span>
                          )}
                          {/* Seller rates buyer */}
                          {!isBuyer && !isRental && ['completed','delivered'].includes(o.status) && (
                            <RateBuyerButton order={o} />
                          )}
                          {/* Generate shipping label — seller only, unshipped orders */}
                          {!isBuyer && !isRental && ['pending','confirmed'].includes(o.status) && !shippingResult[o.id] && (
                            <button className="btn btn-sm" style={{ background:'#1A5276', color:'white', border:'none', borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:700, cursor:'pointer' }}
                              onClick={() => { setShippingOrderId(shippingOrderId === o.id ? null : o.id); setShippingRates([]); setShippingError(null); }}>
                              📦 {shippingOrderId === o.id ? 'Cancel' : 'Generate Label'}
                            </button>
                          )}
                          {shippingResult[o.id] && (
                            <a href={shippingResult[o.id].labelUrl} target="_blank" rel="noreferrer"
                              className="btn btn-sm" style={{ background:'#2D7A3A', color:'white', border:'none', borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:700, textDecoration:'none' }}>
                              ⬇️ Download Label
                            </a>
                          )}
                        </div>

                        {/* Shipping label panel */}
                        {shippingOrderId === o.id && (
                          <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)', background:'#F0F4F8', borderRadius:10, padding:16 }}>
                            <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>📦 Package Details</div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                              {[['weightLbs','Weight (lbs)'],['lengthIn','Length (in)'],['widthIn','Width (in)'],['heightIn','Height (in)']].map(([k,lbl]) => (
                                <div key={k}>
                                  <label style={{ fontSize:11, color:'var(--text-soft)', display:'block', marginBottom:3 }}>{lbl}</label>
                                  <input className="form-input" type="number" min="0.1" step="0.1" style={{ fontSize:13 }}
                                    value={shippingForm[k]} onChange={e => setShippingForm(f => ({ ...f, [k]: e.target.value }))} />
                                </div>
                              ))}
                            </div>
                            <button className="btn btn-cherry btn-sm" disabled={shippingLoading}
                              onClick={() => fetchRates(o)}>
                              {shippingLoading ? '⏳ Getting rates…' : '🔍 Get Rates'}
                            </button>
                            {shippingError && <div style={{ color:'var(--cherry)', fontSize:13, marginTop:8 }}>{shippingError}</div>}
                            {shippingRates.length > 0 && (
                              <div style={{ marginTop:14 }}>
                                <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>Select a rate:</div>
                                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                                  {shippingRates.map(r => (
                                    <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'white', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px' }}>
                                      <div>
                                        <span style={{ fontWeight:700, fontSize:13 }}>{r.carrier}</span>
                                        <span style={{ fontSize:12, color:'var(--text-soft)', marginLeft:8 }}>{r.service}</span>
                                        {r.days && <span style={{ fontSize:11, color:'var(--text-soft)', marginLeft:6 }}>· {r.days} day{r.days!==1?'s':''}</span>}
                                      </div>
                                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                        <span style={{ fontWeight:700, color:'var(--cherry)' }}>${r.rate.toFixed(2)}</span>
                                        <button className="btn btn-sm" style={{ background:'#1A5276', color:'white', border:'none', borderRadius:8, padding:'5px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}
                                          disabled={shippingBuying === r.id}
                                          onClick={() => buyLabel(o, r.id)}>
                                          {shippingBuying === r.id ? '⏳…' : 'Buy'}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Return Requests */}
              {data.returns?.length > 0 && (
                <div style={{ marginTop:28 }}>
                  <h4 style={{ fontWeight:700, fontSize:16, marginBottom:12 }}>↩️ Return Requests</h4>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {data.returns.map(r => {
                      const isSeller = r.seller_id === user.id;
                      return (
                        <div key={r.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                            <div>
                              <div style={{ fontWeight:700 }}>{r.listing_title}</div>
                              <div style={{ fontSize:13, color:'var(--text-soft)' }}>Order #{r.order_number} · Reason: {r.reason?.replace(/_/g,' ')}</div>
                              {r.description && <div style={{ fontSize:12, fontStyle:'italic', color:'var(--text-mid)', marginTop:2 }}>"{r.description}"</div>}
                            </div>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ fontWeight:700 }}>${parseFloat(r.refund_amount||0).toFixed(2)}</div>
                              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background: r.status==='pending'?'#FFF9E6':r.status==='approved'?'#EAF5EC':'#F7F7F7', color: r.status==='pending'?'#C9930A':r.status==='approved'?'#2D7A3A':'#888' }}>{r.status}</span>
                            </div>
                          </div>
                          {isSeller && r.status === 'pending' && (
                            <div style={{ paddingTop:10, borderTop:'1px solid var(--border)' }}>
                              {rejectingReturnId === r.id ? (
                                <div>
                                  <textarea className="form-input" rows={2} value={rejectReturnNote} onChange={e => setRejectReturnNote(e.target.value)} placeholder="Reason for rejection (shown to buyer)..." style={{ fontSize:13, resize:'vertical', marginBottom:8 }} />
                                  <div style={{ display:'flex', gap:8 }}>
                                    <button className="btn btn-sm" style={{ background:'var(--cherry)', color:'white', border:'none', borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:700, cursor:'pointer' }} onClick={() => {
                                      API.patch(`/returns/${r.id}/reject`, { reason: rejectReturnNote })
                                        .then(() => { setData(p => ({ ...p, returns: p.returns.map(x => x.id===r.id ? {...x,status:'rejected'} : x) })); setRejectingReturnId(null); setRejectReturnNote(''); });
                                    }}>❌ Confirm Reject</button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setRejectingReturnId(null)}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display:'flex', gap:8 }}>
                                  <button className="btn btn-cherry btn-sm" onClick={() => API.patch(`/returns/${r.id}/approve`).then(() => setData(p => ({ ...p, returns: p.returns.map(x => x.id===r.id ? {...x,status:'approved'} : x) })))}>✅ Approve Refund</button>
                                  <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={() => { setRejectingReturnId(r.id); setRejectReturnNote(''); }}>❌ Reject</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── MY BIDS ── */}
          {/* ── OFFERS ── */}
          {activeTab === 'offers' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>💰 Offers</h3>

              {/* Incoming offers (seller) */}
              {data.incomingOffers?.length > 0 && (
                <div style={{ marginBottom:32 }}>
                  <h4 style={{ fontWeight:700, fontSize:16, marginBottom:12 }}>📥 Offers on My Listings ({data.incomingOffers.length})</h4>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {data.incomingOffers.map(o => (
                      <div key={o.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
                        <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 }}>
                          {o.primary_image && <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${o.primary_image}`} alt="" style={{ width:48, height:48, borderRadius:8, objectFit:'cover' }} />}
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700 }}>{o.listing_title}</div>
                            <div style={{ fontSize:13, color:'var(--text-soft)' }}>
                              From {o.first_name} {o.last_name} (@{o.username}) · Listed at ${parseFloat(o.listing_price||0).toFixed(2)}
                            </div>
                            {o.message && <div style={{ fontSize:13, fontStyle:'italic', color:'var(--text-mid)', marginTop:4 }}>"{o.message}"</div>}
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:20 }}>${parseFloat(o.amount).toFixed(2)}</div>
                            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background: o.status==='pending'?'#FFF9E6':o.status==='accepted'?'#EAF5EC':'#F7F7F7', color: o.status==='pending'?'#C9930A':o.status==='accepted'?'#2D7A3A':'#888' }}>{o.status}</span>
                          </div>
                        </div>
                        {o.status === 'pending' && (
                          <div>
                            {counteringOfferId === o.id ? (
                              <div style={{ background:'#EBF0FF', border:'1px solid #C5CAE9', borderRadius:10, padding:12, marginTop:8 }}>
                                <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>🔄 Counter-Offer</div>
                                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                                  <input className="form-input" type="number" step="0.01" min="0.01" placeholder="Your price ($)" value={counterAmt} onChange={e => setCounterAmt(e.target.value)} style={{ flex:1, fontSize:13 }} />
                                </div>
                                <input className="form-input" type="text" placeholder="Add a note (optional)" value={counterMsg} onChange={e => setCounterMsg(e.target.value)} style={{ marginBottom:8, fontSize:13 }} />
                                <div style={{ display:'flex', gap:8 }}>
                                  <button className="btn btn-cherry btn-sm" disabled={!counterAmt} onClick={() => {
                                    API.patch(`/offers/${o.id}/counter`, { counterAmount: parseFloat(counterAmt), counterMessage: counterMsg })
                                      .then(() => { setData(p => ({ ...p, incomingOffers: p.incomingOffers.map(x => x.id===o.id ? {...x,status:'countered'} : x) })); setCounteringOfferId(null); setCounterAmt(''); setCounterMsg(''); });
                                  }}>Send Counter</button>
                                  <button className="btn btn-ghost btn-sm" onClick={() => setCounteringOfferId(null)}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                                <button className="btn btn-cherry btn-sm" onClick={() => API.patch(`/offers/${o.id}/accept`).then(() => setData(p => ({ ...p, incomingOffers: p.incomingOffers.map(x => x.id===o.id ? {...x,status:'accepted'} : x) })))}>✅ Accept</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setCounteringOfferId(o.id); setCounterAmt(''); setCounterMsg(''); }}>🔄 Counter</button>
                                <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={() => API.patch(`/offers/${o.id}/decline`).then(() => setData(p => ({ ...p, incomingOffers: p.incomingOffers.map(x => x.id===o.id ? {...x,status:'declined'} : x) })))}>❌ Decline</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* My sent offers (buyer) */}
              <div>
                <h4 style={{ fontWeight:700, fontSize:16, marginBottom:12 }}>📤 My Sent Offers ({data.myOffers?.length || 0})</h4>
                {!data.myOffers?.length ? (
                  <div style={{ textAlign:'center', padding:40, background:'var(--cream)', borderRadius:16 }}>
                    <div style={{ fontSize:40, marginBottom:8 }}>💰</div>
                    <p style={{ color:'var(--text-soft)' }}>No offers sent yet</p>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {data.myOffers.map(o => (
                      <div key={o.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px', display:'flex', gap:12, alignItems:'center' }}>
                        {o.primary_image && <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${o.primary_image}`} alt="" style={{ width:48, height:48, borderRadius:8, objectFit:'cover' }} />}
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700 }}>{o.title}</div>
                          <div style={{ fontSize:13, color:'var(--text-soft)' }}>Your offer: ${parseFloat(o.amount).toFixed(2)}{o.counter_amount ? ` · Counter: $${parseFloat(o.counter_amount).toFixed(2)}` : ''}</div>
                          {o.counter_message && <div style={{ fontSize:12, fontStyle:'italic', color:'var(--text-mid)' }}>"{o.counter_message}"</div>}
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background: o.status==='pending'?'#FFF9E6':o.status==='accepted'?'#EAF5EC':o.status==='countered'?'#EBF0FF':'#F7F7F7', color: o.status==='pending'?'#C9930A':o.status==='accepted'?'#2D7A3A':o.status==='countered'?'#1A5276':'#888' }}>{o.status}</span>
                          {o.status === 'countered' && (
                            <button className="btn btn-cherry btn-sm" onClick={() => API.patch(`/offers/${o.id}/accept-counter`).then(() => setData(p => ({ ...p, myOffers: p.myOffers.map(x => x.id===o.id ? {...x,status:'accepted'} : x) })))}>✅ Accept Counter</button>
                          )}
                          {o.status === 'pending' && (
                            <button className="btn btn-ghost btn-sm" onClick={() => API.patch(`/offers/${o.id}/cancel`).then(() => setData(p => ({ ...p, myOffers: p.myOffers.map(x => x.id===o.id ? {...x,status:'cancelled'} : x) })))}>Cancel</button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${o.listing_id}`)}>View Listing</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'bids' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>My Bids</h3>
              {!data.bids?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🔨</div>
                  <p style={{ color:'var(--text-soft)', marginBottom:16 }}>You haven't placed any bids yet</p>
                  <button className="btn btn-cherry" onClick={()=>navigate('/browse')}>Browse Auctions</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {data.bids.map(b => {
                    const auctionEnded = new Date(b.auction_ends_at) < new Date();
                    const timeLeft = Math.max(0, Math.ceil((new Date(b.auction_ends_at) - new Date()) / 60000));
                    const hoursLeft = Math.floor(timeLeft / 60);
                    const minsLeft = timeLeft % 60;
                    return (
                      <div key={b.id} style={{ background:'white', border:`1px solid ${b.is_winning?'var(--green)':'var(--border)'}`, borderRadius:14, padding:'18px 20px' }}>
                        <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
                          {/* Thumbnail */}
                          <div style={{ width:64, height:64, borderRadius:10, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 }}>
                            {b.primary_image
                              ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${b.primary_image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                              : '🔨'
                            }
                          </div>

                          {/* Details */}
                          <div style={{ flex:1, minWidth:160 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, fontSize:15 }}>{b.title}</span>
                              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100,
                                background: b.is_winning ? '#EAF5EC' : '#FEF3E8',
                                color: b.is_winning ? '#2D7A3A' : '#8B5A00' }}>
                                {b.is_winning ? '✅ Winning' : b.status === 'outbid' ? '⚠️ Outbid' : '🔨 Active'}
                              </span>
                            </div>
                            <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:4 }}>
                              by {b.seller_first_name} {b.seller_last_name} · {format(new Date(b.created_at),'MMM d, yyyy')}
                            </div>
                            <div style={{ fontSize:12, color:'var(--text-mid)' }}>
                              Your bid: ${parseFloat(b.amount).toFixed(2)} • Current: ${parseFloat(b.highest_bid || b.current_bid || 0).toFixed(2)}
                            </div>
                            {!auctionEnded && (
                              <div style={{ fontSize:12, color: timeLeft <= 60 ? '#C0392B' : 'var(--text-soft)', marginTop:4, fontWeight: timeLeft <= 60 ? 700 : 400 }}>
                                ⏱ {hoursLeft > 0 ? `${hoursLeft}h ${minsLeft}m` : `${minsLeft}m`} left
                              </div>
                            )}
                            {auctionEnded && (
                              <div style={{ fontSize:12, color:'#C0392B', marginTop:4, fontWeight:700 }}>
                                Auction ended
                              </div>
                            )}
                          </div>

                          {/* Price + Actions */}
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
                            <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:18 }}>
                              ${parseFloat(b.highest_bid || b.current_bid || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Action buttons */}
                        {!auctionEnded && !b.is_winning && (
                          <div style={{ display:'flex', gap:8, marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)' }}>
                            <button className="btn btn-cherry btn-sm"
                              onClick={() => navigate(`/listing/${b.listing_id}`)}>
                                🔨 Increase Bid
                            </button>
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => navigate(`/listing/${b.listing_id}`)}>
                                👁 View Listing
                            </button>
                          </div>
                        )}
                        {(auctionEnded || b.is_winning) && (
                          <div style={{ display:'flex', gap:8, marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)' }}>
                            <button className="btn btn-ghost btn-sm"
                              onClick={() => navigate(`/listing/${b.listing_id}`)}>
                                👁 View Listing
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── AUCTIONS WON ── */}
          {activeTab === 'auctions-won' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>🏆 Auctions Won</h3>
              </div>
              {!data.auctions?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🔨</div>
                  <p style={{ color:'var(--text-soft)' }}>You haven't won any auctions yet</p>
                  <button className="btn btn-cherry" style={{ marginTop:16 }} onClick={() => navigate('/browse?type=auction')}>Browse Auctions</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {data.auctions.map(a => {
                    const needsPayment = a.status === 'pending';
                    return (
                      <div key={a.id} style={{ background:'white', border:`1px solid ${needsPayment ? 'var(--cherry)' : 'var(--border)'}`, borderRadius:14, padding:'18px 20px', position:'relative', overflow:'hidden' }}>
                        {needsPayment && (
                          <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'var(--cherry)' }} />
                        )}
                        <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
                          {/* Thumbnail */}
                          <div style={{ width:72, height:72, borderRadius:10, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32 }}>
                            {a.primary_image
                              ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${a.primary_image}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                              : '🔨'
                            }
                          </div>
                          {/* Details */}
                          <div style={{ flex:1, minWidth:160 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                              <span style={{ fontWeight:700, fontSize:15 }}>{a.title}</span>
                              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:'#FFF3E0', color:'#E65100' }}>
                                🏆 Won
                              </span>
                              {needsPayment && (
                                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:'#FDECEA', color:'var(--cherry)', animation:'pulse 2s infinite' }}>
                                  ⚠️ Payment required
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:4 }}>
                              Seller: {a.seller_first_name} {a.seller_last_name} · {format(new Date(a.created_at), 'MMM d, yyyy')}
                            </div>
                            <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                              Order #{a.order_number}
                            </div>
                          </div>
                          {/* Price + status */}
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
                            <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:20, color:'var(--cherry)' }}>
                              ${parseFloat(a.total_charged || a.item_price || 0).toFixed(2)}
                            </div>
                            {statusPill(a.status)}
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div style={{ display:'flex', gap:8, marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)', flexWrap:'wrap' }}>
                          {needsPayment && (
                            <button className="btn btn-cherry btn-sm" onClick={() => {
                              addToCart(
                                { id: a.listing_id, title: a.title, listing_type: 'auction', price: a.item_price, seller_id: a.seller_id, primary_image: a.primary_image },
                                { existingOrderId: a.id, existingOrderNumber: a.order_number }
                              );
                              navigate('/checkout');
                            }}>
                              💳 Complete Purchase
                            </button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/orders/${a.id}`)}>
                            🧾 View Receipt
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${a.listing_id}`)}>
                            🔗 View Listing
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── BARTER TRADES ── */}
          {activeTab === 'barter' && (
            <BarterTradesPage />
          )}

          {/* ── FREE CLAIMS ── */}
          {activeTab === 'free' && (
            <FreeClaimsPage />
          )}

          {/* ── SAVED ── */}
          {/* ── PAYOUTS ── */}
          {activeTab === 'payouts' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>💸 Earnings & Payouts</h3>
              </div>

              {/* Stats row */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:14, marginBottom:28 }}>
                {[
                  { label:'Total Earned', value: data.totalEarned||0, color:'#2D7A3A', bg:'#EAF5EC' },
                  { label:'Available to Withdraw', value: data.availableAmount||0, color:'#1A5276', bg:'#EBF0FF' },
                  { label:'In Escrow / Pending', value: data.pendingAmount||0, color:'#C9930A', bg:'#FFF9E6' },
                ].map(s => (
                  <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.color}22`, borderRadius:14, padding:'18px 20px', textAlign:'center' }}>
                    <div style={{ fontSize:26, fontWeight:700, fontFamily:"'Playfair Display',serif", color:s.color }}>${parseFloat(s.value).toFixed(2)}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Stripe Connect */}
              {data.stripeConnect && !data.stripeConnect.payoutsEnabled && (
                <div style={{ background:'#EBF0FF', border:'1px solid #C5CAE9', borderRadius:14, padding:20, marginBottom:24 }}>
                  <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>🏦 Set up your payout account</div>
                  <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:14 }}>Connect a bank account via Stripe to receive your earnings. Takes about 2 minutes.</div>
                  <button className="btn btn-cherry" disabled={stripeLoading} onClick={async () => {
                    setStripeLoading(true);
                    try {
                      const { data: d } = await API.post('/users/me/stripe-connect');
                      if (d.url) { window.location.href = d.url; }
                      else { alert('Setup link not returned. Check Stripe Connect is enabled on your account.'); setStripeLoading(false); }
                    } catch (err) { alert(err.response?.data?.error || err.message || 'Failed to start setup'); setStripeLoading(false); }
                  }}>{stripeLoading ? 'Redirecting…' : '🏦 Connect Bank Account'}</button>
                </div>
              )}
              {data.stripeConnect?.payoutsEnabled && (
                <div style={{ background:'#EAF5EC', border:'1px solid #81C784', borderRadius:14, padding:16, marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:20 }}>✅</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14 }}>Payout account connected</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Earnings are automatically sent to your bank account after order completion.</div>
                  </div>
                </div>
              )}

              {/* Recent Sales Earnings */}
              {data.recentSales?.length > 0 && (
                <div style={{ marginBottom:32 }}>
                  <h4 style={{ fontSize:16, fontWeight:700, marginBottom:12 }}>📦 Recent Sales</h4>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {data.recentSales.map(s => {
                      const statusColor = { completed:'#2D7A3A', held_escrow:'#C9930A', pending:'#888' };
                      const hasEarning  = s.net_amount != null;
                      return (
                        <div key={s.order_id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                          <div style={{ flex:1, minWidth:140 }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>{s.listing_title}</div>
                            <div style={{ fontSize:11, color:'var(--text-soft)' }}>{new Date(s.created_at).toLocaleDateString()}</div>
                          </div>
                          {hasEarning ? (
                            <div style={{ textAlign:'right', fontSize:12 }}>
                              <div style={{ fontWeight:700 }}>${parseFloat(s.net_amount).toFixed(2)} <span style={{ fontWeight:400, color:'var(--text-soft)' }}>net</span></div>
                              <div style={{ color:'var(--text-soft)' }}>${parseFloat(s.gross_amount).toFixed(2)} − ${parseFloat(s.platform_fee).toFixed(2)} fee</div>
                            </div>
                          ) : (
                            <div style={{ fontSize:12, color:'var(--text-soft)' }}>Pending payment</div>
                          )}
                          <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:'#F3F4F6', color: statusColor[s.txn_status] || '#888', textTransform:'capitalize' }}>
                            {(s.txn_status || s.order_status || '').replace(/_/g,' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Withdrawal History */}
              <h4 style={{ fontSize:16, fontWeight:700, marginBottom:12 }}>🏦 Withdrawal History</h4>
              {!data.payouts?.length ? (
                <div style={{ textAlign:'center', padding:32, background:'var(--cream)', borderRadius:14 }}>
                  <p style={{ color:'var(--text-soft)' }}>No withdrawals yet. Earnings will appear here once paid out.</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {data.payouts.map(p => (
                    <div key={p.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                      <div>
                        <div style={{ fontWeight:700 }}>${parseFloat(p.amount).toFixed(2)}</div>
                        <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                          {new Date(p.created_at).toLocaleDateString()}
                          {p.paid_at && ` · Paid ${new Date(p.paid_at).toLocaleDateString()}`}
                          {p.bank_last4 && ` · ···${p.bank_last4}`}
                        </div>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:p.status==='paid'?'#EAF5EC':p.status==='processing'?'#EBF0FF':'#FFF9E6', color:p.status==='paid'?'#2D7A3A':p.status==='processing'?'#1A5276':'#C9930A', textTransform:'capitalize' }}>{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── FOLLOWING ── */}
          {activeTab === 'following' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>👥 Sellers I Follow ({data.following?.length||0})</h3>
              {!data.following?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>👥</div>
                  <p style={{ color:'var(--text-soft)', marginBottom:16 }}>You're not following any sellers yet.</p>
                  <button className="btn btn-cherry" onClick={() => navigate('/browse')}>Browse Sellers</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {data.following.map(seller => (
                    <div key={seller.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                      <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                        <div onClick={() => navigate(`/seller/${seller.id}`)} style={{ width:52, height:52, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0, cursor:'pointer' }}>
                          {seller.avatar_url
                            ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${seller.avatar_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            : seller.first_name?.[0]?.toUpperCase()}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <div style={{ fontWeight:700, fontSize:16, cursor:'pointer' }} onClick={() => navigate(`/seller/${seller.id}`)}>{seller.first_name} {seller.last_name}</div>
                              {seller.username && <div style={{ fontSize:12, color:'var(--text-soft)' }}>@{seller.username}</div>}
                              <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:2 }}>
                                {seller.rating_avg ? `⭐ ${parseFloat(seller.rating_avg).toFixed(1)} (${seller.rating_count} reviews)` : 'No reviews yet'}
                                {seller.follower_count > 0 && ` · 👥 ${seller.follower_count} followers`}
                                {seller.id_verified && ' · ✅ ID Verified'}
                              </div>
                            </div>
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/seller/${seller.id}`)}>View Profile</button>
                              <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={() =>
                                API.post(`/users/${seller.id}/follow`).then(() => setData(p => ({ ...p, following: p.following.filter(s => s.id !== seller.id) })))
                              }>Unfollow</button>
                            </div>
                          </div>
                          {seller.recent_listings?.length > 0 && (
                            <div style={{ marginTop:12 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>Recent Listings</div>
                              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                                {seller.recent_listings.map(l => (
                                  <div key={l.id} onClick={() => navigate(`/listing/${l.slug || l.id}`)} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'var(--cream)', borderRadius:8, cursor:'pointer', border:'1px solid var(--border)' }}>
                                    {l.primary_image && <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt="" style={{ width:32, height:32, borderRadius:6, objectFit:'cover', flexShrink:0 }} />}
                                    <div>
                                      <div style={{ fontSize:12, fontWeight:600, maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.title}</div>
                                      <div style={{ fontSize:11, color:'var(--cherry)', fontWeight:700 }}>${parseFloat(l.price).toFixed(2)}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── FOLLOWING FEED ── */}
          {activeTab === 'feed' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:8 }}>🗞️ Following Feed</h3>
              <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:24 }}>New listings from sellers you follow — last 30 days.</p>
              {!data.feedListings?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🗞️</div>
                  <p style={{ color:'var(--text-soft)', marginBottom:16 }}>
                    {data.feedListings ? 'No new listings from people you follow.' : 'Follow some sellers to see their new listings here.'}
                  </p>
                  <button className="btn btn-cherry" onClick={() => navigate('/browse')}>Browse Sellers</button>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:16 }}>
                  {data.feedListings.map(l => (
                    <div key={l.id} onClick={() => navigate(`/listing/${l.slug || l.id}`)}
                      style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', cursor:'pointer', transition:'transform .15s,box-shadow .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                      <div style={{ height:150, background:'var(--cream)', position:'relative', overflow:'hidden' }}>
                        {l.primary_image
                          ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.primary_image}`} alt={l.title} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                          : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', fontSize:40, opacity:.3 }}>📦</div>
                        }
                        <div style={{ position:'absolute', top:8, left:8, background:'rgba(0,0,0,.6)', color:'white', fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:5, textTransform:'uppercase', letterSpacing:'1px' }}>
                          {l.listing_type === 'rent' ? 'Rent' : l.listing_type === 'auction' ? 'Auction' : l.listing_type === 'sale' ? 'Sale' : l.listing_type}
                        </div>
                      </div>
                      <div style={{ padding:'12px 14px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
                          <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--cherry)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:10, fontWeight:700, flexShrink:0, overflow:'hidden' }}>
                            {l.avatar_url ? <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${l.avatar_url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : (l.first_name||'?')[0]}
                          </div>
                          <span style={{ fontSize:11, color:'var(--text-soft)' }}>{l.first_name} {l.last_name}</span>
                        </div>
                        <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3, marginBottom:6, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{l.title}</div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:16, color:'var(--cherry)' }}>
                          ${parseFloat(l.price||0).toLocaleString()}
                          {l.listing_type === 'rent' && <span style={{ fontSize:11, fontWeight:400, color:'var(--text-soft)', fontFamily:"'DM Sans',sans-serif" }}>/day</span>}
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:4 }}>
                          {new Date(l.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── BUNDLES ── */}
          {activeTab === 'bundles' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:8 }}>🎁 Bundle Deals</h3>
              <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:24 }}>Offer buyers a discount when they purchase multiple items from you.</p>

              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, maxWidth:520 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15 }}>Bundle Discount</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:2 }}>Status: <span style={{ fontWeight:700, color: data.bundleDiscount ? '#2D7A3A' : '#888' }}>{data.bundleDiscount ? '✅ Active' : '⭕ Inactive'}</span></div>
                  </div>
                </div>

                {data.bundleDiscount ? (
                  <div style={{ background:'#EAF5EC', border:'1px solid #81C784', borderRadius:10, padding:16, marginBottom:16 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'#2D7A3A', marginBottom:4 }}>
                      {data.bundleDiscount.discount_percent}% off when buying {data.bundleDiscount.min_items}+ items
                    </div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Buyers will automatically see this discount at checkout.</div>
                  </div>
                ) : (
                  <div style={{ background:'var(--cream)', borderRadius:10, padding:16, marginBottom:16, fontSize:13, color:'var(--text-soft)' }}>
                    No bundle discount active. Set one up below to attract buyers purchasing multiple items.
                  </div>
                )}

                <BundleForm existing={data.bundleDiscount} onSave={(bd) => setData(p => ({ ...p, bundleDiscount: bd }))} onDelete={() => setData(p => ({ ...p, bundleDiscount: null }))} />
              </div>

              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, maxWidth:520, marginTop:20 }}>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:10 }}>💡 How it works</div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {[
                    { icon:'1️⃣', text:'Set a minimum number of items (e.g. 2) and a discount percentage.' },
                    { icon:'2️⃣', text:'When a buyer has items from you in their cart, they see your bundle offer.' },
                    { icon:'3️⃣', text:'The discount is applied automatically at checkout.' },
                  ].map(s => (
                    <div key={s.icon} style={{ display:'flex', gap:10, alignItems:'flex-start', fontSize:13 }}>
                      <span style={{ fontSize:18 }}>{s.icon}</span>
                      <span style={{ color:'var(--text-mid)' }}>{s.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── TEMPLATES ── */}
          {activeTab === 'templates' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>📋 Listing Templates</h3>
                <button className="btn btn-cherry btn-sm" onClick={() => navigate('/list')}>+ New Listing</button>
              </div>
              <div style={{ background:'#EBF0FF', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13 }}>
                <strong>How templates work:</strong> When creating a listing, fill in your standard details then click <strong>💾 Save as Template</strong>. Next time, load any template to pre-fill the form and change only what's different.
              </div>
              {!data.templates?.length ? (
                <div style={{ textAlign:'center', padding:'60px 40px', background:'var(--cream)', borderRadius:16, border:'1px dashed var(--border)' }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
                  <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:20 }}>No templates yet. Create a listing and save it as a template.</p>
                  <button className="btn btn-cherry" onClick={() => navigate('/list')}>Create a Listing</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {data.templates.map(t => {
                    const d = typeof t.data === 'string' ? JSON.parse(t.data) : (t.data || {});
                    return (
                      <div key={t.id} style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                        <div style={{ flex:1, minWidth:160 }}>
                          <div style={{ fontWeight:700, fontSize:15 }}>{t.name}</div>
                          <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:2 }}>
                            {[
                              d.listingType && d.listingType.charAt(0).toUpperCase() + d.listingType.slice(1),
                              d.title,
                              d.price && `$${parseFloat(d.price).toLocaleString()}`,
                              d.condition,
                            ].filter(Boolean).join(' · ')}
                          </div>
                          <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:2 }}>
                            Saved {new Date(t.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <button className="btn btn-cherry btn-sm" onClick={() => navigate('/list', { state: { templateData: d } })}>
                            Use Template
                          </button>
                          <button className="btn btn-ghost btn-sm" style={{ color:'var(--cherry)' }} onClick={() => {
                            if (!window.confirm(`Delete template "${t.name}"?`)) return;
                            API.delete(`/users/me/templates/${t.id}`)
                              .then(() => setData(p => ({ ...p, templates: p.templates.filter(x => x.id !== t.id) })));
                          }}>🗑️</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'saved' && (
            <div>
              <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, marginBottom:20 }}>Saved Listings ({data.listings?.length||0})</h3>
              {!data.listings?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>❤️</div>
                  <p style={{ color:'var(--text-soft)', marginBottom:16 }}>Nothing saved yet</p>
                  <button className="btn btn-cherry" onClick={()=>navigate('/browse')}>Browse Listings</button>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:18 }}>
                  {data.listings.map(l => <ListingCard key={l.id} listing={{...l,isSaved:true}} />)}
                </div>
              )}
            </div>
          )}

          {/* ── NOTIFICATIONS ── */}
          {activeTab === 'notifications' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700 }}>Notifications</h3>
                {data.notifications?.some(n=>!n.is_read) && (
                  <button className="btn btn-ghost btn-sm" onClick={markAllRead}>Mark all read</button>
                )}
              </div>

              {/* Push notification opt-in */}
              {push.supported && push.permission !== 'denied' && (
                <div style={{ background: push.subscribed ? '#EAF5EC' : '#FFF8F0', border: `1px solid ${push.subscribed ? '#4CAF5044' : 'var(--border)'}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                      {push.subscribed ? '🔔 Push notifications on' : '🔔 Enable push notifications'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                      {push.subscribed ? 'You\'ll get alerts for orders, bids, and messages.' : 'Get instant alerts for orders, bids, messages and shipping updates.'}
                    </div>
                  </div>
                  <button
                    className={push.subscribed ? 'btn btn-ghost btn-sm' : 'btn btn-cherry btn-sm'}
                    disabled={push.loading}
                    onClick={push.subscribed ? push.unsubscribe : push.subscribe}>
                    {push.loading ? '⏳…' : push.subscribed ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              )}
              {push.permission === 'denied' && (
                <div style={{ background: '#FFF3E0', border: '1px solid #FFB30044', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#E65100' }}>
                  🚫 Push notifications are blocked in your browser settings. Enable them in Site Settings to receive alerts.
                </div>
              )}
              {!data.notifications?.length ? (
                <div style={{ textAlign:'center', padding:'60px', background:'var(--cream)', borderRadius:16 }}>
                  <div style={{ fontSize:48, marginBottom:12 }}>🔔</div>
                  <p style={{ color:'var(--text-soft)' }}>No notifications yet</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {data.notifications.map(n => {
                    const nData = (() => { try { return typeof n.data === 'string' ? JSON.parse(n.data) : n.data; } catch { return {}; } })();
                    const orderTypes = ['order_placed','item_sold','auction_won','auction_ended'];
                    const hasOrder = orderTypes.includes(n.type) && nData?.orderId;
                    const hasListing = nData?.listingId;
                    const notifLink = n.link || (hasOrder ? `/orders/${nData.orderId}` : null);
                    return (
                      <div key={n.id} style={{ background:n.is_read?'white':'#FDECEA', border:`1px solid ${n.is_read?'var(--border)':'rgba(192,57,43,.2)'}`, borderRadius:12, padding:'14px 16px' }}>
                        <div style={{ display:'flex', gap:14, alignItems:'flex-start', cursor:notifLink?'pointer':'default' }}
                          onClick={() => notifLink && navigate(notifLink)}>
                          <span style={{ fontSize:20 }}>
                            {n.type==='order_placed'?'🛒':n.type==='item_sold'?'💰':n.type==='auction_won'?'🔨':n.type==='new_message'?'💬':'🔔'}
                          </span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{n.title}</div>
                            {n.body && <div style={{ fontSize:13, color:'var(--text-mid)' }}>{n.body}</div>}
                            <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:4 }}>{format(new Date(n.created_at),'MMM d, h:mm a')}</div>
                          </div>
                          {!n.is_read && <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--cherry)', flexShrink:0, marginTop:4 }} />}
                        </div>
                        {(hasOrder || hasListing) && (
                          <div style={{ display:'flex', gap:8, marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
                            {hasOrder && (
                              <button className="btn btn-cherry btn-sm" onClick={() => navigate(`/orders/${nData.orderId}`)}>
                                🧾 View Receipt
                              </button>
                            )}
                            {hasListing && (
                              <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/listing/${nData.listingId}`)}>
                                🔗 View Listing
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── PROFILE ── */}
          {activeTab === 'profile' && (
            <div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                <a href={`/profile/${user?.id}`} style={{ fontSize:13, fontWeight:600, color:'var(--cherry)', textDecoration:'none', border:'1px solid var(--cherry)', borderRadius:8, padding:'6px 14px' }}>
                  🏪 View My Storefront →
                </a>
              </div>
              <ProfileSettings user={user} onUpdate={() => {}} />
            </div>
          )}

          {/* ── BILLING ── */}
          {activeTab === 'billing' && (
            <BillingSettings user={user} />
          )}

          {/* ── SECURITY ── */}
          {activeTab === 'security' && (
            <div>
              <TwoFactorSetup />

              {/* ID Verification */}
              <div style={{ marginTop:24, maxWidth:600 }}>
                {/* Approved */}
                {(user.idVerified || data.idVerification?.status === 'approved') && (
                  <div style={{ background:'#EAF5EC', border:'1px solid #A9D7B2', borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, color:'#2D7A3A', fontSize:16 }}>✅ Identity Verified</div>
                    <div style={{ fontSize:13, color:'#2D7A3A', marginTop:4 }}>Your identity has been verified. You display the Verified badge on your profile.</div>
                  </div>
                )}

                {/* Pending review */}
                {!user.idVerified && data.idVerification?.status === 'pending' && (
                  <div style={{ background:'#FFF8E1', border:'1px solid #FFD54F', borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, color:'#E65100', fontSize:16 }}>⏳ Verification Under Review</div>
                    <div style={{ fontSize:13, color:'#BF360C', marginTop:4 }}>Your documents were submitted on {new Date(data.idVerification.created_at).toLocaleDateString()}. Admin will review within 24–48 hours.</div>
                  </div>
                )}

                {/* Rejected — show reason + resubmit form */}
                {!user.idVerified && data.idVerification?.status === 'rejected' && (
                  <div style={{ background:'#FFF3E0', border:'1px solid #FF8A65', borderRadius:14, padding:20, marginBottom:16 }}>
                    <div style={{ fontWeight:700, color:'#BF360C', fontSize:15 }}>❌ Verification Rejected</div>
                    {data.idVerification.notes && <div style={{ fontSize:13, color:'#BF360C', marginTop:4 }}>Reason: {data.idVerification.notes}</div>}
                    <div style={{ fontSize:13, color:'#BF360C', marginTop:4 }}>Please resubmit with clearer photos.</div>
                  </div>
                )}

                {/* Upload form — shown when no submission or rejected */}
                {!user.idVerified && (!data.idVerification || data.idVerification?.status === 'rejected') && (
                  <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginTop: data.idVerification?.status === 'rejected' ? 0 : 0 }}>
                    <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>🪪 ID Verification</div>
                    <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:20 }}>Upload a government-issued ID to earn the Verified badge and build buyer trust.</div>

                    <div className="form-group">
                      <label className="form-label">Document Type</label>
                      <select className="form-input" value={idDocType} onChange={e => setIdDocType(e.target.value)}>
                        <option value="drivers_license">Driver's License</option>
                        <option value="passport">Passport</option>
                        <option value="national_id">National ID</option>
                      </select>
                    </div>

                    {[
                      { field:'front', label:'Front of ID', required:true, url:idFrontUrl, setUrl:setIdFrontUrl },
                      { field:'back',  label:'Back of ID (optional)', required:false, url:idBackUrl, setUrl:setIdBackUrl },
                      { field:'selfie',label:'Selfie holding your ID (optional)', required:false, url:idSelfieUrl, setUrl:setIdSelfieUrl },
                    ].map(({ field, label, required, url }) => (
                      <div key={field} className="form-group">
                        <label className="form-label">{label}{required && <span className="required"> *</span>}</label>
                        {url ? (
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${url}`} alt={label} style={{ height:64, borderRadius:8, objectFit:'cover', border:'1px solid var(--border)' }} />
                            <button className="btn btn-ghost btn-sm" onClick={() => {
                              if (field==='front') setIdFrontUrl('');
                              if (field==='back') setIdBackUrl('');
                              if (field==='selfie') setIdSelfieUrl('');
                            }}>Remove</button>
                          </div>
                        ) : (
                          <label style={{ display:'block', border:'2px dashed var(--border)', borderRadius:10, padding:'20px 16px', textAlign:'center', cursor:'pointer', background:'var(--cream)' }}>
                            {idUploading[field] ? '⏳ Uploading…' : `📎 Click to upload ${label}`}
                            <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => uploadIdDoc(e.target.files[0], field)} />
                          </label>
                        )}
                      </div>
                    ))}

                    {idMsg && (
                      <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:12, background: idMsg.type==='error'?'#FFEBEE':'#EAF5EC', color: idMsg.type==='error'?'#C62828':'#2D7A3A', fontSize:13 }}>
                        {idMsg.text}
                      </div>
                    )}

                    <button className="btn btn-cherry" disabled={!idFrontUrl || idSubmitting} onClick={async () => {
                      setIdSubmitting(true);
                      setIdMsg(null);
                      try {
                        await API.post('/users/me/verify-id', { docType:idDocType, docFrontUrl:idFrontUrl, docBackUrl:idBackUrl||null, selfieUrl:idSelfieUrl||null });
                        setIdMsg({ type:'success', text:'Submitted! Admin will review within 24–48 hours.' });
                        setIdFrontUrl(''); setIdBackUrl(''); setIdSelfieUrl('');
                        setData(p => ({ ...p, idVerification: { status:'pending', created_at: new Date().toISOString(), notes:null } }));
                      } catch (err) {
                        setIdMsg({ type:'error', text: err.response?.data?.error || 'Submission failed. Please try again.' });
                      }
                      setIdSubmitting(false);
                    }}>
                      {idSubmitting ? '⏳ Submitting…' : 'Submit for Verification'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Listing Modal */}
      <EditListingModal
        listing={editingListing}
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingListing(null); }}
        onSave={() => {
          setData(p => ({
            ...p,
            listings: p.listings.map(l => l.id === editingListing.id ? editingListing : l)
          }));
          setShowEditModal(false);
          setEditingListing(null);
        }}
        onDelete={() => {
          setData(p => ({
            ...p,
            listings: p.listings.filter(l => l.id !== editingListing.id)
          }));
          setShowEditModal(false);
          setEditingListing(null);
        }}
      />

      <PromotionPurchase
        listing={editingListing}
        isOpen={showPromotionPurchase}
        onClose={() => { setShowPromotionPurchase(false); setEditingListing(null); }}
        onPurchase={() => {
          setShowPromotionPurchase(false);
          setEditingListing(null);
        }}
      />
    </div>
  );
}
